import { Aedu } from 'global';

import { NoThanksText } from 'js/bulk_download/all_readers_products_helper';
import { isCoronavirusWork } from 'js/coronavirus/coronavirus_research_detector';
import showModal from 'js/components/show_modal';

import {
  BulkDownloadableWork,
  bulkDownloadDownloadSource,
  bulkDownloadImpressionSource,
  BulkDownloadTrigger,
  giveGatedDownloadOnUpsellClose,
  navigateToCheckout,
  updateBulkDownloadLookback,
} from './bulk_download_helper';
import TransactionalPricingModal from './TransactionalPricingModal/TransactionalPricingModal';
import {
  fetchBulkDownloadCountsCached,
  fetchPreviewWorksCached,
} from './swp_pdf_pack/bulk_download_counts_cache';
import { loadWonderchatScript } from './wonderchat_widget';
import { abTest as TransactionalPricingV2AbTest } from 'js/ab_tests/tests/bulk_download_2026_q1_transactional_pricing_v2';

// LISWP triggers eligible for the transactional pricing A/B test.
// Only these triggers (regular BD upsell on LISWP) should bucket users.
const TRANSACTIONAL_PRICING_TRIGGERS: ReadonlySet<string> = new Set([
  'swp-toolbar-work-card-download-button',
  'swp-bulk-download-button',
  'swp-sticky-header-work-card-download-button',
  'swp-sticky-header-bulk-download-button',
]);

type Options = {
  trigger: BulkDownloadTrigger;
  work: BulkDownloadableWork;
  noThanksText: NoThanksText;
  // `gatedDownload` indicates whether the single-work download should be gated
  // behind the upsell (i.e., user gets the download when the modal closes).
  gatedDownload?: boolean;
  autoStartBulkDownload?: boolean;
  purchased?: boolean;
  readerMessageLimitReached?: boolean;
  setReaderMessageLimitReached?: (value: boolean) => void;
};

export async function showBulkDownloadModal({
  trigger,
  work,
  gatedDownload,
  autoStartBulkDownload,
  purchased,
  readerMessageLimitReached = true,
  setReaderMessageLimitReached = () => {},
}: Options): Promise<void> {
  const isPremium: boolean = Aedu.User?.current?.premium;
  const coronavirusWork = isCoronavirusWork(work);
  const shouldStartWorkDownloadOnUpsellClose = gatedDownload === true;

  if (isPremium || coronavirusWork || purchased) {
    import(
      './WorkCardsBulkDownloadUpsellModal/PremiumWorkCardsBulkDownloadModal'
    ).then((module) => {
      showModal(
        module.default,
        {
          sourceWork: {
            id: work.id,
            title: work.title,
            slug: work.slug,
          },
          downloadImmediately: autoStartBulkDownload,
        },
        {
          modalWrapperClass: 'bulk-download-modal-new-blue',
          modalClassName: 'bulk-download-modal',
          disableModalContentClass: true,
          fullScreenMobile: true,
        },
      );

      if (isPremium && trigger.startsWith('swp-')) {
        loadWonderchatScript();
      }
    });
  } else {
    // Read transactional pricing eligibility from the counts cache.
    // The cache is populated on page load so this await resolves immediately.
    const countsData = await fetchBulkDownloadCountsCached(Number(work.id));

    // Only bucket users who are eligible AND triggered from LISWP
    // regular upsell (not lookback/fallback)
    const eligibleForTransactionalPricing =
      countsData.eligible_for_single_purchase === true &&
      TRANSACTIONAL_PRICING_TRIGGERS.has(trigger);
    const transactionalPricingBucket = eligibleForTransactionalPricing
      ? TransactionalPricingV2AbTest()
      : undefined;
    const isTransactionalPricingVariant =
      transactionalPricingBucket === 'variant';

    const afterFirstModalClose = () => {
      if (isTransactionalPricingVariant) {
        // Show the transactional pricing modal.
        // fetchPreviewWorksCached is already settled (the first modal
        // fetched it), so both promises resolve almost immediately.
        const workIdNum = Number(work.id);
        fetchPreviewWorksCached(workIdNum)
          .catch(() => [])
          .then((bundleWorks) => {
            const sourceWork = bundleWorks.find(
              (w) => w.bucket === 'thePaperItself',
            );
            showModal(
              TransactionalPricingModal,
              {
                workId: work.id,
                workTitle: work.title,
                workThumbnailUrl: sourceWork?.thumbnailUrl,
                pdfCount: countsData.total_bulk_download_count,
                priceInfo: countsData.single_purchase_price_info,
              },
              {
                modalWrapperClass: 'bulk-download-modal-new-blue',
                modalClassName: 'bulk-download-modal',
                disableModalContentClass: true,
                center: true,
                afterClose: () => {
                  if (shouldStartWorkDownloadOnUpsellClose) {
                    maybeDownloadWork(
                      trigger,
                      work,
                      readerMessageLimitReached,
                      setReaderMessageLimitReached,
                    );
                  }
                  updateBulkDownloadLookback(trigger);
                },
              },
            );
          });
      } else {
        if (shouldStartWorkDownloadOnUpsellClose) {
          maybeDownloadWork(
            trigger,
            work,
            readerMessageLimitReached,
            setReaderMessageLimitReached,
          );
        }
        updateBulkDownloadLookback(trigger);
      }
    };

    // When the user clicks the CTA, the modal snapshots the current filter
    // state and work IDs into sessionStorage (see
    // bulk_download_checkout_state.ts). After checkout, the premium modal
    // consumes that snapshot and uses the saved IDs for the auto-download so
    // the user gets exactly what they saw before upgrading. Storage is keyed
    // by the source work ID, has a 30-min TTL, and is cleared on dismiss.
    import('./WorkCardsBulkDownloadUpsellModal').then((upsellModule) => {
      showModal(
        upsellModule.default,
        {
          onPrimaryCtaClicked: () =>
            navigateToCheckout(trigger, work.id, new Set()),
          impressionSource: bulkDownloadImpressionSource('upsell', trigger),
          sourceWork: work,
          transactionalPricingVariant: eligibleForTransactionalPricing
            ? isTransactionalPricingVariant
            : undefined,
        },
        {
          modalWrapperClass: 'bulk-download-modal-new-blue',
          modalClassName: 'bulk-download-modal',
          disableModalContentClass: true,
          fullScreenMobile: true,
          afterClose: afterFirstModalClose,
        },
      );
    });
  }
}

function maybeDownloadWork(
  trigger: BulkDownloadTrigger,
  work: BulkDownloadableWork,
  readerMessageLimitReached: boolean,
  setReaderMessageLimitReached: (value: boolean) => void,
) {
  if (!giveGatedDownloadOnUpsellClose(trigger)) return;
  const link = document.createElement('a');
  link.href = work.attachment.url;
  link.setAttribute('target', '_blank');
  link.setAttribute('style', 'display: none');
  document.body.appendChild(link);
  link.click();

  // Skip reader message modal if no authors (system-owned works)
  const primaryAuthor = work.authors[0];
  if (readerMessageLimitReached || !primaryAuthor) return;

  import(
    'js/core_webpack/sitewide_business_elements/download/reader_message_form.wjs'
  ).then((module) => {
    showModal(
      module.default,
      {
        primaryAuthor: {
          displayName: primaryAuthor.displayName,
          photo: primaryAuthor.photo,
        },
        workId: work.id,
        attachmentId: work.attachment.id,
        readerMessageSource: bulkDownloadDownloadSource(trigger),
        showMessageToast: false,
        setReaderMessageLimitReached: setReaderMessageLimitReached,
      },
      { modalClassName: 'wide-modal' },
    );
  });
}
